import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GradeIcon from '@mui/icons-material/Grade';
import ReadOnlySimulator from '../quiz/ReadOnlySimulator';
import {
  getQuizById,
  getQuizQuestions,
  getQuizLinksByQuizId,
  getQuizResponses,
  gradeResponse,
  type Quiz,
  type QuizQuestion,
  type QuizLink,
  type QuizResponse
} from '../../services/quizService';

interface QuizResponseGraderProps {
  quizId: string;
  onBack: () => void;
}

interface ParticipantData {
  link: QuizLink;
  responses: QuizResponse[];
}

const QuizResponseGrader: React.FC<QuizResponseGraderProps> = ({ quizId, onBack }) => {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gradingInProgress, setGradingInProgress] = useState<string | null>(null);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);

  useEffect(() => {
    loadQuizData();
  }, [quizId]);

  const loadQuizData = async () => {
    try {
      setLoading(true);
      const [quizData, questionsData, linksData] = await Promise.all([
        getQuizById(quizId),
        getQuizQuestions(quizId),
        getQuizLinksByQuizId(quizId)
      ]);

      // Load responses for each completed link
      const completedLinks = linksData.filter(link => link.completed_at);
      const participantsData: ParticipantData[] = await Promise.all(
        completedLinks.map(async (link) => {
          const responses = await getQuizResponses(link.id);
          return { link, responses };
        })
      );

      setQuiz(quizData);
      setQuestions(questionsData);
      setParticipants(participantsData);
      setError(null);
    } catch (err) {
      console.error('Error loading quiz data:', err);
      setError('Failed to load quiz data');
    } finally {
      setLoading(false);
    }
  };

  const handleGrade = async (responseId: string, isCorrect: boolean) => {
    try {
      setGradingInProgress(responseId);
      await gradeResponse(responseId, isCorrect);

      // Update only the graded response in state without full reload
      setParticipants(prevParticipants =>
        prevParticipants.map(p => ({
          ...p,
          responses: p.responses.map(r =>
            r.id === responseId
              ? { ...r, is_correct: isCorrect, graded_at: new Date().toISOString() }
              : r
          )
        }))
      );

      setError(null);
    } catch (err) {
      console.error('Error grading response:', err);
      setError('Failed to grade response');
    } finally {
      setGradingInProgress(null);
    }
  };

  const autoGradeMultipleChoice = async (responseId: string, question: QuizQuestion, response: QuizResponse) => {
    if (question.question_type !== 'multiple_choice') return;

    const isCorrect = response.response_text === question.accepted_answer;
    await handleGrade(responseId, isCorrect);
  };

  const getOptionText = (question: QuizQuestion, optionId: string): string => {
    if (question.question_type !== 'multiple_choice' || !question.options) {
      return optionId;
    }
    const option = question.options.find(opt => opt.id === optionId);
    return option ? option.text : optionId;
  };

  const getQuestionById = (questionId: string): QuizQuestion | undefined => {
    return questions.find(q => q.id === questionId);
  };

  const getResponseForQuestion = (responses: QuizResponse[], questionId: string): QuizResponse | undefined => {
    return responses.find(r => r.question_id === questionId);
  };

  const calculateScore = (responses: QuizResponse[]): { correct: number; total: number; pending: number } => {
    const graded = responses.filter(r => r.is_correct !== null && r.is_correct !== undefined);
    const correct = graded.filter(r => r.is_correct).length;
    const pending = responses.length - graded.length;
    return { correct, total: responses.length, pending };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // If a participant is selected, show detailed grading view
  if (selectedParticipantId) {
    const selectedParticipant = participants.find(p => p.link.id === selectedParticipantId);
    if (!selectedParticipant) {
      setSelectedParticipantId(null);
      return null;
    }

    const score = calculateScore(selectedParticipant.responses);

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => setSelectedParticipantId(null)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
              {selectedParticipant.link.participant_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {quiz?.title} - Grading Responses
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={`${score.correct}/${score.total}`}
              color={score.pending > 0 ? 'default' : 'success'}
            />
            {score.pending > 0 && (
              <Chip
                label={`${score.pending} pending`}
                color="warning"
              />
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {questions.map((question, index) => {
            const response = getResponseForQuestion(selectedParticipant.responses, question.id);
            if (!response) return null;

            const isGraded = response.is_correct !== null && response.is_correct !== undefined;

            return (
              <Card key={question.id} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                      Question {index + 1}
                    </Typography>
                    {isGraded && (
                      <Chip
                        icon={response.is_correct ? <CheckCircleIcon /> : <CancelIcon />}
                        label={response.is_correct ? 'Correct' : 'Incorrect'}
                        color={response.is_correct ? 'success' : 'error'}
                        size="small"
                      />
                    )}
                  </Box>

                  <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                    {question.question_text}
                  </Typography>

                  {question.simulator_state && question.simulator_state.length > 0 && (
                    <Box sx={{ my: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <ReadOnlySimulator playerStates={question.simulator_state} />
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ flex: 1 }}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Student's Answer
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {response.response_text
                            ? question.question_type === 'multiple_choice'
                              ? getOptionText(question, response.response_text)
                              : response.response_text
                            : <em>No response provided</em>}
                        </Typography>
                      </Paper>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Paper sx={{ p: 2, bgcolor: 'rgba(46, 125, 50, 0.15)' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Accepted Answer
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {question.question_type === 'multiple_choice'
                            ? getOptionText(question, question.accepted_answer)
                            : question.accepted_answer}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
                    {question.question_type === 'multiple_choice' && !isGraded && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => autoGradeMultipleChoice(response.id, question, response)}
                        disabled={gradingInProgress === response.id}
                      >
                        Auto Grade
                      </Button>
                    )}
                    {question.question_type === 'free_text' && (
                      <ButtonGroup variant="contained" disabled={gradingInProgress === response.id}>
                        <Button
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleGrade(response.id, true)}
                          variant={isGraded && response.is_correct ? 'contained' : 'outlined'}
                        >
                          Correct
                        </Button>
                        <Button
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleGrade(response.id, false)}
                          variant={isGraded && !response.is_correct ? 'contained' : 'outlined'}
                        >
                          Incorrect
                        </Button>
                      </ButtonGroup>
                    )}
                  </Box>

                  {isGraded && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      Graded on {formatDate(response.graded_at!)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!quiz) {
    return (
      <Box>
        <Alert severity="error">Quiz not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
          Back to Quizzes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            {quiz.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Grade Responses
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {participants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No completed submissions yet. Responses will appear here once participants submit their quizzes.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Participant</strong></TableCell>
                <TableCell><strong>Score</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Completed</strong></TableCell>
                <TableCell><strong>Time Taken</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map((participant) => {
                const score = calculateScore(participant.responses);
                return (
                  <TableRow key={participant.link.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {participant.link.participant_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${score.correct}/${score.total}`}
                        color={score.pending > 0 ? 'default' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {score.pending > 0 ? (
                        <Chip
                          label={`${score.pending} pending`}
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="Complete"
                          color="success"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(participant.link.completed_at!)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {participant.link.time_taken_minutes?.toFixed(1)} min
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<GradeIcon />}
                        onClick={() => setSelectedParticipantId(participant.link.id)}
                      >
                        Grade
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Overall Summary */}
      {participants.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Overall Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Submissions
              </Typography>
              <Typography variant="h5">
                {participants.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Fully Graded
              </Typography>
              <Typography variant="h5" color="success.main">
                {participants.filter(p => calculateScore(p.responses).pending === 0).length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Pending Grading
              </Typography>
              <Typography variant="h5" color="warning.main">
                {participants.filter(p => calculateScore(p.responses).pending > 0).length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Avg Score (graded only)
              </Typography>
              <Typography variant="h5">
                {(() => {
                  const allScores = participants
                    .map(p => calculateScore(p.responses))
                    .filter(s => s.pending === 0);
                  if (allScores.length === 0) return '-';
                  const avgPercentage = (allScores.reduce((sum, s) => sum + (s.correct / s.total), 0) / allScores.length) * 100;
                  return `${avgPercentage.toFixed(1)}%`;
                })()}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default QuizResponseGrader;
